/**
 * Admin Table Components
 * Reusable table, pagination, filters, and bulk actions
 */

import React, { useState } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  Search,
  Filter,
  X,
  Check,
  MoreVertical
} from 'lucide-react';
import { AdminEmptyState, AdminSearchEmptyState } from './AdminStates';

/**
 * Admin Table
 */
export const AdminTable = ({
  columns,
  data,
  onRowClick,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  loading = false,
  emptyState,
  className = '',
}) => {
  const allSelected = data.length > 0 && selectedRows.length === data.length;
  const someSelected = selectedRows.length > 0 && selectedRows.length < data.length;

  if (!loading && data.length === 0) {
    return emptyState || <AdminEmptyState title="No data" message="No items to display." />;
  }

  return (
    <div className={`bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden ${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="admin-table">
          <thead className="bg-zinc-800/50">
            <tr>
              {onSelectRow && (
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => el && (el.indeterminate = someSelected)}
                    onChange={(e) => onSelectAll?.(e.target.checked)}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-purple-500 focus:ring-purple-500"
                  />
                </th>
              )}
              {columns.map((col, i) => (
                <th 
                  key={col.key || i}
                  className={`px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider ${col.className || ''}`}
                  style={{ width: col.width }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {data.map((row, rowIndex) => {
              const rowKey = row.id || row._id || rowIndex;
              const isSelected = selectedRows.includes(rowKey);
              
              return (
                <tr 
                  key={rowKey}
                  onClick={() => onRowClick?.(row)}
                  className={`
                    ${onRowClick ? 'cursor-pointer hover:bg-zinc-800/50' : ''}
                    ${isSelected ? 'bg-purple-500/10' : ''}
                    transition-colors
                  `}
                  data-testid={`admin-table-row-${rowKey}`}
                >
                  {onSelectRow && (
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => onSelectRow(rowKey, e.target.checked)}
                        className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-purple-500 focus:ring-purple-500"
                      />
                    </td>
                  )}
                  {columns.map((col, colIndex) => (
                    <td 
                      key={col.key || colIndex}
                      className={`px-4 py-3 text-sm ${col.cellClassName || ''}`}
                    >
                      {col.render ? col.render(row, rowIndex) : row[col.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Admin Pagination
 */
export const AdminPagination = ({
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  onLimitChange,
  showLimitSelector = true,
  className = '',
}) => {
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const pages = [];
  const maxVisiblePages = 5;
  let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className={`flex items-center justify-between py-4 ${className}`} data-testid="admin-pagination">
      <div className="flex items-center gap-4">
        <span className="text-sm text-zinc-400">
          Showing {start}-{end} of {total}
        </span>
        
        {showLimitSelector && onLimitChange && (
          <select
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-white"
          >
            <option value={10}>10 / page</option>
            <option value={25}>25 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        )}
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(1)}
          disabled={page === 1}
          className="p-2 rounded hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="First page"
        >
          <ChevronsLeft className="w-4 h-4" />
        </button>
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className="p-2 rounded hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => onPageChange(1)}
              className="px-3 py-1 rounded hover:bg-zinc-800 transition-colors"
            >
              1
            </button>
            {startPage > 2 && <span className="px-2 text-zinc-500">...</span>}
          </>
        )}

        {pages.map((p) => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={`px-3 py-1 rounded transition-colors ${
              p === page 
                ? 'bg-purple-600 text-white' 
                : 'hover:bg-zinc-800'
            }`}
          >
            {p}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-2 text-zinc-500">...</span>}
            <button
              onClick={() => onPageChange(totalPages)}
              className="px-3 py-1 rounded hover:bg-zinc-800 transition-colors"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          className="p-2 rounded hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={page === totalPages}
          className="p-2 rounded hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Last page"
        >
          <ChevronsRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/**
 * Admin Filters
 */
export const AdminFilters = ({
  filters = [],
  values = {},
  onChange,
  onSearch,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasActiveFilters = filters.some(f => values[f.key] && values[f.key] !== 'all');

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center gap-4 flex-wrap">
        {/* Search */}
        {onSearchChange && (
          <form onSubmit={(e) => { e.preventDefault(); onSearch?.(); }} className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-purple-500"
              data-testid="admin-search-input"
            />
            {searchValue && (
              <button
                type="button"
                onClick={() => { onSearchChange(''); onSearch?.(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </form>
        )}

        {/* Filter toggles */}
        {filters.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {filters.map((filter) => (
              <select
                key={filter.key}
                value={values[filter.key] || 'all'}
                onChange={(e) => onChange(filter.key, e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                data-testid={`admin-filter-${filter.key}`}
              >
                {filter.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ))}
            
            {hasActiveFilters && (
              <button
                onClick={() => {
                  filters.forEach(f => onChange(f.key, 'all'));
                }}
                className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Admin Bulk Actions
 */
export const AdminBulkActions = ({
  selectedCount,
  actions = [],
  onClearSelection,
  className = '',
}) => {
  if (selectedCount === 0) return null;

  return (
    <div 
      className={`bg-purple-600/20 border border-purple-500/30 rounded-lg px-4 py-3 flex items-center justify-between ${className}`}
      data-testid="admin-bulk-actions"
    >
      <div className="flex items-center gap-3">
        <span className="text-sm text-purple-300">
          {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
        </span>
        <button
          onClick={onClearSelection}
          className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
        >
          Clear selection
        </button>
      </div>
      <div className="flex items-center gap-2">
        {actions.map((action, i) => (
          <button
            key={i}
            onClick={action.onClick}
            disabled={action.disabled}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              action.variant === 'danger'
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-zinc-700 hover:bg-zinc-600 text-white'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {action.icon && <action.icon className="w-4 h-4 inline mr-1.5" />}
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Admin Action Menu (for row actions)
 */
export const AdminActionMenu = ({
  actions = [],
  trigger,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        className="p-2 hover:bg-zinc-700 rounded transition-colors"
      >
        {trigger || <MoreVertical className="w-4 h-4" />}
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)} 
          />
          <div className="absolute right-0 top-full mt-1 z-20 bg-zinc-800 border border-zinc-700 rounded-lg shadow-lg py-1 min-w-[160px]">
            {actions.map((action, i) => (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick();
                  setIsOpen(false);
                }}
                disabled={action.disabled}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-zinc-700 transition-colors flex items-center gap-2 ${
                  action.variant === 'danger' ? 'text-red-400 hover:text-red-300' : 'text-zinc-300'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {action.icon && <action.icon className="w-4 h-4" />}
                {action.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default {
  AdminTable,
  AdminPagination,
  AdminFilters,
  AdminBulkActions,
  AdminActionMenu,
};
